import React from "react";
import Layout from "@/components/Layout";

const TOS_VERSION = "2026.02.1";

// NOTE TO FOUNDER: This is a template starting draft. HAVE AN ATTORNEY REVIEW
// BEFORE CHARGING CUSTOMERS. Replace [YOUR COMPANY NAME] and [YOUR STATE] below.

export default function Terms() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 prose prose-stone">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Legal</div>
        <h1 className="text-4xl font-bold tracking-tight">Terms of Service</h1>
        <p className="text-stone-500 text-sm">Version {TOS_VERSION} · Last updated Feb 2026</p>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 my-6 text-sm text-amber-900">
          <b>Template notice:</b> This Terms of Service is a starting draft. Before accepting paid
          customers, have it reviewed by a qualified attorney familiar with SaaS contracts and
          food-regulatory law.
        </div>

        <Section title="1. Acceptance of Terms">
          By creating an account or using Alaine's Bench™ ("the Service"), you ("User") agree to be bound
          by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
        </Section>

        <Section title="2. Nature of the Service — NOT Legal, Regulatory, or Laboratory Advice">
          Alaine's Bench is a calculation and formatting tool that generates informational nutrition-label
          and ingredient-statement output based on data you and third parties provide. <b>The Service
          is not, and does not purport to be, a substitute for:</b>
          <ul>
            <li>Laboratory analysis of finished products;</li>
            <li>Review by a qualified regulatory professional or food scientist;</li>
            <li>Legal advice from a licensed food-law attorney;</li>
            <li>Certification by any government agency, including the U.S. Food and Drug Administration.</li>
          </ul>
          You are solely responsible for verifying the accuracy of all label content, independently
          confirming FDA compliance (including 21 CFR 101 and the Food Allergen Labeling and Consumer
          Protection Act as amended by the FASTER Act), and obtaining laboratory analysis before
          commercial distribution of any product.
        </Section>

        <Section title="3. Disclaimer of Warranties">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE MAXIMUM EXTENT PERMITTED BY LAW,
          [YOUR COMPANY NAME] DISCLAIMS ALL WARRANTIES INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, NON-INFRINGEMENT, AND QUIET
          ENJOYMENT. We do not warrant that the Service will be error-free, uninterrupted, or that
          calculated values will match laboratory-analyzed values for your specific product.
        </Section>

        <Section title="4. Limitation of Liability">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL [YOUR COMPANY NAME], ITS OFFICERS,
          EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR
          PUNITIVE DAMAGES — INCLUDING BUT NOT LIMITED TO LOST PROFITS, LOST REVENUE, RECALL COSTS,
          REGULATORY FINES, OR PRODUCT LIABILITY CLAIMS — ARISING OUT OF OR RELATED TO YOUR USE OF
          THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR AGGREGATE LIABILITY
          TO YOU FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT OF FEES PAID BY YOU TO US IN THE TWELVE
          (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
        </Section>

        <Section title="5. User Responsibilities & Indemnification">
          You agree that you are solely responsible for:
          <ul>
            <li>The accuracy of all ingredient data, weights, and supplier specifications you enter;</li>
            <li>Verifying allergen declarations against your actual manufacturing environment, including cross-contact risk;</li>
            <li>Confirming that any nutrient content claim, health claim, or structure-function claim you make on your product complies with applicable FDA regulations;</li>
            <li>Obtaining laboratory analysis of your finished product before commercial distribution;</li>
            <li>Retaining qualified regulatory and legal counsel for your product line.</li>
          </ul>
          You agree to indemnify and hold harmless [YOUR COMPANY NAME] against all third-party claims,
          damages, liabilities, losses, and reasonable attorneys' fees arising from: (i) your products
          or labels; (ii) your breach of these Terms; (iii) your violation of any law or the rights
          of a third party.
        </Section>

        <Section title="6. Access & Accounts">
          Alaine's Bench™ is a proprietary platform operated by Alaine &amp; Co. and is not offered
          as a public subscription service. Accounts are provisioned by Alaine &amp; Co. for its
          consulting clients and collaborators as part of an engagement. Any fees for consulting
          services are governed by your separate engagement agreement with Alaine &amp; Co. Access
          may be modified or withdrawn when the related engagement ends.
        </Section>

        <Section title="7. Data & Privacy">
          Your use of the Service is also governed by our Privacy Policy. You retain ownership of
          formulations, ingredient data, and other content you upload ("User Content"). You grant us a
          limited license to process User Content solely for the purpose of operating the Service.
        </Section>

        <Section title="8. Termination">
          We may suspend or terminate your account immediately for any breach of these Terms,
          nonpayment, fraudulent activity, or conduct that exposes us to liability. Upon termination,
          your right to access the Service ceases.
        </Section>

        <Section title="9. Changes to These Terms">
          We may modify these Terms with 30 days' notice by email or in-app notification. Continued
          use of the Service after the effective date constitutes acceptance of the revised Terms.
        </Section>

        <Section title="10. Governing Law & Disputes">
          These Terms are governed by the laws of [YOUR STATE], USA, without regard to its conflict
          of law provisions. Any dispute shall be resolved by binding arbitration administered by
          the American Arbitration Association under its Commercial Arbitration Rules, except that
          either party may seek injunctive relief in court for intellectual-property matters.
        </Section>

        <Section title="11. Contact">
          Questions about these Terms may be sent to [YOUR CONTACT EMAIL].
        </Section>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold text-stone-900">{title}</h2>
      <div className="text-stone-700 text-sm leading-relaxed mt-2">{children}</div>
    </div>
  );
}
